import type { DREHierarchicalLine, DREHierarchicalNode, DRELineType, DREParentPath } from "../../../types/dashboard";
import type { ExpandNodeParams } from "./index";

type DRERowItem = DREHierarchicalLine | DREHierarchicalNode;

const isNode = (item: DRERowItem): item is DREHierarchicalNode => {
  return "nodeType" in item;
};

const extractOriginalId = (id: string | number): string => String(id);

/**
 * Sprint 19 - US-18464: Build parent path for DRE hierarchy drill-down
 * Hierarchy: CATEGORIA → SUBCATEGORIA → REGIONAL → CLIENTE → VIAGEM → VEICULO
 */
const buildParentPath = (
  item: DREHierarchicalNode,
  parentPath?: DREParentPath
): DREParentPath => {
  const newPath: DREParentPath = { ...parentPath };
  const originalId = extractOriginalId(item.id);

  switch (item.nodeType) {
    case "CATEGORIA":
      newPath.categoryId = parseInt(originalId, 10) || undefined;
      break;
    case "SUBCATEGORIA":
      // Sprint 19: New hierarchy level
      newPath.subcategoryId = parseInt(originalId, 10) || undefined;
      break;
    case "REGIONAL":
    case "SEGMENTO":
      // Sprint 19: Regional is the new name for segment in the hierarchy
      newPath.segmentId = parseInt(originalId, 10) || undefined;
      break;
    case "FILIAL":
      newPath.branchId = parseInt(originalId, 10) || undefined;
      break;
    case "CLIENTE":
    case "FORNECEDOR":
      newPath.clientOrSupplierId = parseInt(originalId, 10) || undefined;
      break;
    case "VIAGEM":
      newPath.tripCode = originalId;
      break;
    case "VEICULO":
      newPath.vehicleId = originalId;
      break;
  }

  return newPath;
};

interface DRERowProps {
  item: DRERowItem;
  lineType: DRELineType;
  expandedItems: string[];
  loadingNodes: string[];
  onToggle: (id: string) => void;
  onExpandNode: (params: ExpandNodeParams) => Promise<void>;
  parentPath?: DREParentPath;
  isLast?: boolean;
  depth?: number;
  setItemId: (id: string) => void;
  hoveredId?: string;
}

const getIndentStyle = (level: number): React.CSSProperties => {
  const basePadding = 25;
  const paddingPerLevel = 25;
  const paddingLeft = basePadding + (level * paddingPerLevel);
  return { paddingLeft: `${paddingLeft}px` };
};

export default function DRERow({
  item,
  lineType,
  expandedItems,
  loadingNodes,
  onToggle,
  onExpandNode,
  parentPath,
  isLast = false,
  depth = 0,
  hoveredId,
  setItemId
}: DRERowProps) {

  const itemCompositeId = item.compositeId ?? item.id;
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedItems.includes(itemCompositeId);
  const isLoading = loadingNodes.includes(itemCompositeId);
  const indentStyle = getIndentStyle(depth);
  const isBold =
    item.label === "Lucro Operacional" ||
    item.label === "Margem de Lucro" ||
    item.label === "(=) Contribution Margin"
  const isNegative = item.value < 0;
  const canFetchChildren = isNode(item) && item.expandable && (!item.children || item.children.length === 0);

  const isForecastValueNegative = item?.totals?.forecastValue < 0
  const isCommittedValueNegative = item?.totals?.committedValue < 0
  const isActualValueNegative = item?.totals?.actualValue < 0
  const isForecastVarianceNegative = item?.totals?.forecastVariance < 0

  const handleToggle = async () => {
    if (isExpanded) {
      onToggle(itemCompositeId);
      return;
    }

    if (canFetchChildren && isNode(item)) {
      const originalId = extractOriginalId(item.id);
      await onExpandNode({
        nodeId: originalId,
        compositeId: itemCompositeId,
        nodeType: item.nodeType,
        lineType,
        parentPath,
      });
    }

    onToggle(itemCompositeId);
  };

  const childParentPath = isNode(item) ? buildParentPath(item, parentPath) : parentPath;

  return (
    <>
      <div
        key={itemCompositeId}
        className={
          `
            display-flex align-items-center justify-content-between gap-10 padding-y-10 bg-white border
            ${isLast ? "border-none" : "border-bottom-only"}
            ${hoveredId === itemCompositeId ? "bg-highlight-lightest" : "bg-white"}
          `
        }
        onMouseMove={() => {
          setItemId(itemCompositeId);
        }}
        onMouseOut={() => {
          setItemId('');
        }}
      >
        <div className="flex-1-1" style={indentStyle}>
          <span className={`text-color-darker ${isBold ? "text-bold" : ""}`}>{item.label}</span>
        </div>

        <div className="display-flex">
          <div className={`width-150 display-flex justify-content-end ${isForecastValueNegative ? "text-color-danger" : "text-color-darker"} ${isBold ? "text-bold" : ""}`}>
            {item?.totals?.forecastFormattedValue || 'R$ 0.00'}
          </div>
          <div className={`width-150 display-flex justify-content-end ${isCommittedValueNegative ? "text-color-danger" : "text-color-darker"} ${isBold ? "text-bold" : ""}`}>
            {item?.totals?.committedFormattedValue || 'R$ 0.00'}
          </div>
          <div className={`width-150 display-flex justify-content-end ${isActualValueNegative ? "text-color-danger" : "text-color-darker"} ${isBold ? "text-bold" : ""}`}>
            {item?.totals?.actualFormattedValue || 'R$ 0.00'}
          </div>
          <div className={`width-150 display-flex justify-content-end ${isForecastVarianceNegative ? "text-color-danger" : "text-color-darker"} ${isBold ? "text-bold" : ""}`}>
            {item?.totals?.forecastVarianceFormatted || 'R$ 0.00'}
          </div>
        </div>

        {(hasChildren || canFetchChildren) ? (
          <span
            className={`cursor-pointer margin-left-10 ${isLoading ? "opacity-50" : ""}`}
            onClick={isLoading ? undefined : handleToggle}
          >
            {isLoading ? (
              <span className="rioglyph rioglyph-spinner spinning" />
            ) : (
              <span className={`rioglyph ${isExpanded ? "rioglyph-chevron-up" : "rioglyph-chevron-down"}`} />
            )}
          </span>
        ) : (
          <span className="margin-left-25" />
        )}
      </div>

      {hasChildren &&
        isExpanded &&
        item.children!.map((child, index, array) => (
          <DRERow
            item={child}
            key={child.compositeId ?? child.id}
            lineType={lineType}
            onToggle={onToggle}
            expandedItems={expandedItems}
            loadingNodes={loadingNodes}
            onExpandNode={onExpandNode}
            parentPath={childParentPath}
            isLast={isLast && index === array.length - 1}
            depth={depth + 1}
            hoveredId={hoveredId}
            setItemId={setItemId}
          />
        ))}
    </>
  );
}
